// Production breakdown helpers. Data stays where Categories & Elements always
// stored it: JSON in data-category-list / data-element-list on each
// p.sceneHeadings node. Predefined categories are virtual (never written)
// until a user stores something under them.

export const PREDEFINED_CATEGORIES = [
    { name: 'Props', color: '#E8A33D' },
    { name: 'Animals', color: '#8D6E63' },
    { name: 'Vehicles', color: '#546E7A' },
    { name: 'Cast', color: '#0099FF' },
    { name: 'Background / Extras', color: '#4FC3F7' },
    { name: 'Costumes / Wardrobe', color: '#AB47BC' },
    { name: 'Hair & Makeup', color: '#F06292' },
    { name: 'Locations', color: '#43A047' },
    { name: 'Set Dressing', color: '#9CCC65' },
    { name: 'Special Effects', color: '#FF7043' },
    { name: 'Visual Effects (VFX)', color: '#7E57C2' },
    { name: 'Stunts', color: '#E53935' },
    { name: 'Weapons', color: '#424242' },
    { name: 'Sound / Playback', color: '#26A69A' },
    { name: 'Equipment', color: '#78909C' },
    { name: 'Other', color: '#BDBDBD' },
]

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')

// Alternative spellings existing users may already have saved.
const ALIASES = {
    prop: 'Props', animal: 'Animals', vehicle: 'Vehicles', cars: 'Vehicles',
    actors: 'Cast', castmembers: 'Cast', extras: 'Background / Extras', background: 'Background / Extras',
    costumes: 'Costumes / Wardrobe', costume: 'Costumes / Wardrobe', wardrobe: 'Costumes / Wardrobe',
    hairmakeup: 'Hair & Makeup', makeup: 'Hair & Makeup', hairandmakeup: 'Hair & Makeup', makeuphair: 'Hair & Makeup',
    location: 'Locations', setdressing: 'Set Dressing', specialeffects: 'Special Effects', sfx: 'Special Effects',
    visualeffects: 'Visual Effects (VFX)', vfx: 'Visual Effects (VFX)', stunt: 'Stunts', weapon: 'Weapons',
    sound: 'Sound / Playback', playback: 'Sound / Playback', soundplayback: 'Sound / Playback', music: 'Sound / Playback',
    equipments: 'Equipment',
}

// Canonical key for a category name (predefined names and aliases collapse).
export const categoryKey = (name) => {
    const n = norm(name)
    const pre = PREDEFINED_CATEGORIES.find(c => norm(c.name) === n)
    if (pre) return norm(pre.name)
    if (ALIASES[n]) return norm(ALIASES[n])
    return n
}

export const isPredefined = (name) => PREDEFINED_CATEGORIES.some(c => norm(c.name) === categoryKey(name))

const parse = (node, attr) => {
    try { return JSON.parse(node.getAttribute(attr) || 'null') || [] } catch { return [] }
}
const sceneNodes = (editor) => editor ? editor.dom.select('p.sceneHeadings') : []

// Build the script-wide production breakdown.
export const getProduction = (editor) => {
    const nodes = sceneNodes(editor)
    const scenes = nodes.map((n, i) => ({ index: i, title: n.textContent }))
    const cats = new Map()
    PREDEFINED_CATEGORIES.forEach(c => cats.set(norm(c.name), { ...c, predefined: true, elements: [] }))

    nodes.forEach(node => parse(node, 'data-category-list').forEach(c => {
        const key = categoryKey(c.name)
        if (!key) return
        if (cats.has(key)) {
            const ex = cats.get(key)
            if (!ex.stored && c.color) { ex.color = c.color; ex.stored = true }
            ex.storedNames = [...new Set([...(ex.storedNames || []), c.name])]
        } else cats.set(key, { name: c.name, color: c.color || '#d9dee3', predefined: false, stored: true, storedNames: [c.name], elements: [] })
    }))

    nodes.forEach((node, sceneIndex) => parse(node, 'data-element-list').forEach(el => {
        let key = categoryKey(el.category)
        if (!key || !cats.has(key)) {
            if (key) cats.set(key, { name: el.category, color: '#d9dee3', predefined: false, storedNames: [el.category], elements: [] })
            else key = norm('Other')
        }
        const cat = cats.get(key)
        let entry = cat.elements.find(e => norm(e.name) === norm(el.name))
        if (!entry) { entry = { name: el.name, notes: el.notes || '', categoryName: cat.name, scenes: [] }; cat.elements.push(entry) }
        if (!entry.notes && el.notes) entry.notes = el.notes
        if (!entry.scenes.includes(sceneIndex)) entry.scenes.push(sceneIndex)
    }))

    return { scenes, categories: [...cats.values()] }
}

const write = (node, attr, list) => node.setAttribute(attr, JSON.stringify(list))

// Store a custom category on the first scene heading so it survives with the script.
export const addCustomCategory = (editor, { name, color }) => {
    const nodes = sceneNodes(editor)
    if (!nodes.length) return 'Add a scene heading first so the category can be saved with the script.'
    if (!name.trim()) return 'Enter a category name.'
    const { categories } = getProduction(editor)
    if (categories.some(c => categoryKey(c.name) === categoryKey(name))) return 'That category already exists.'
    const list = parse(nodes[0], 'data-category-list')
    list.push({ name: name.trim(), color, isDefault: false })
    write(nodes[0], 'data-category-list', list)
    editor.fire('Change')
    return null
}

export const renameCustomCategory = (editor, category, { name, color }) => {
    const newName = name.trim()
    if (!newName) return 'Enter a category name.'
    const { categories } = getProduction(editor)
    if (categoryKey(newName) !== categoryKey(category.name) && categories.some(c => categoryKey(c.name) === categoryKey(newName)))
        return 'That category already exists.'
    const key = categoryKey(category.name)
    let stored = false
    sceneNodes(editor).forEach(node => {
        const cl = parse(node, 'data-category-list')
        if (cl.some(c => categoryKey(c.name) === key)) {
            stored = true
            write(node, 'data-category-list', cl.map(c => categoryKey(c.name) === key ? { ...c, name: newName, color } : c))
        }
        const el = parse(node, 'data-element-list')
        if (el.some(e => categoryKey(e.category) === key))
            write(node, 'data-element-list', el.map(e => categoryKey(e.category) === key ? { ...e, category: newName } : e))
    })
    if (!stored) {
        const first = sceneNodes(editor)[0]
        if (first) write(first, 'data-category-list', [...parse(first, 'data-category-list'), { name: newName, color, isDefault: false }])
    }
    editor.fire('Change')
    return null
}

// Removes the custom category; its elements move to "Other" so nothing is lost.
export const deleteCustomCategory = (editor, category) => {
    const key = categoryKey(category.name)
    sceneNodes(editor).forEach(node => {
        const cl = parse(node, 'data-category-list')
        if (cl.some(c => categoryKey(c.name) === key)) write(node, 'data-category-list', cl.filter(c => categoryKey(c.name) !== key))
        const el = parse(node, 'data-element-list')
        if (el.some(e => categoryKey(e.category) === key))
            write(node, 'data-element-list', el.map(e => categoryKey(e.category) === key ? { ...e, category: 'Other' } : e))
    })
    editor.fire('Change')
}

// Save an element (new or edited) as one item linked to the chosen scenes.
export const saveElement = (editor, { name, notes, categoryName, scenes }, original = null) => {
    const nodes = sceneNodes(editor)
    if (!nodes.length) return 'Add a scene heading first so the element can be saved with the script.'
    if (!name.trim()) return 'Enter an element name.'
    if (!scenes.length) return 'Choose at least one scene.'
    const key = categoryKey(categoryName)
    const matches = (e, n, k) => norm(e.name) === norm(n) && categoryKey(e.category) === k
    const { categories } = getProduction(editor)
    const clash = categories.find(c => categoryKey(c.name) === key)?.elements
        .find(e => norm(e.name) === norm(name) && !(original && norm(original.name) === norm(name) && categoryKey(original.categoryName) === key))
    if (clash) return 'That element already exists in this category.'
    // Newly linked scenes reuse the shared element's permanent ID instead of creating a copy.
    const sharedId = original && nodes.map(n => parse(n, 'data-element-list')
        .find(e => e.elementId && matches(e, original.name, categoryKey(original.categoryName)))).find(Boolean)?.elementId
    nodes.forEach((node, i) => {
        let list = parse(node, 'data-element-list')
        let prev = null
        if (original) {
            const ok = categoryKey(original.categoryName)
            prev = list.find(e => matches(e, original.name, ok))
            list = list.filter(e => !matches(e, original.name, ok))
        }
        if (scenes.includes(i)) list.push({ ...(prev || {}), ...(sharedId && !prev?.elementId ? { elementId: sharedId } : {}), name: name.trim(), category: categoryName, notes })
        write(node, 'data-element-list', list)
    })
    editor.fire('Change')
    return null
}

export const deleteProductionElement = (editor, element) => {
    const key = categoryKey(element.categoryName)
    sceneNodes(editor).forEach(node => {
        const list = parse(node, 'data-element-list')
        const next = list.filter(e => !(norm(e.name) === norm(element.name) && categoryKey(e.category) === key))
        if (next.length !== list.length) write(node, 'data-element-list', next)
    })
    editor.fire('Change')
}
